import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { throttle } from 'lodash-es';
import type { MapMouseEvent, MapboxGeoJSONFeature } from 'mapbox-gl';
import { WellPopup } from 'components/map/well-popup';
import { mapSearchRefAtom } from '../atoms';
import { wellsConfig } from '../datasets/wells.config';
import { docLayerId } from './tile-layer-utils';
import { queryFeaturesNear } from './feature-hit-test';

// The well dot/marker layers a click can land on (config `hitTarget`); the
// survey/stick lines are excluded so a click targets the dot. Index is kept
// because docLayerId embeds it in the layer id.
const WELL_DOT_LAYER_IDS = (wellsConfig.tileLayers ?? []).flatMap(
  (layer, idx) => (layer.hitTarget ? [docLayerId(layer, idx)] : [])
);

// A single click can cover a dense low-zoom cluster; cap the popover list so it
// stays bounded. The nearest dots are kept (see the distance sort), so the cap
// is deterministic rather than mapbox render order.
const MAX_WELLS_PER_CLICK = 25;

interface ClickedWell {
  wells: Record<string, undefined>;
  longitude: number;
  latitude: number;
}

// A feature's [lng, lat], but only for a real Point — a LineString/MultiPoint
// would destructure to arrays and anchor the popover at NaN.
const pointCoords = (f: MapboxGeoJSONFeature): [number, number] | null => {
  const g = f.geometry;
  if (g?.type !== 'Point' || !Array.isArray(g.coordinates)) return null;
  const [lng, lat] = g.coordinates;
  return typeof lng === 'number' && typeof lat === 'number' ? [lng, lat] : null;
};

// Click a well dot on the Map Query map to open the shared main-map well
// popover (WellPopup self-fetches by UWI and carries the 3D viewer). The wells
// tiles already carry `uwi`, so the click only reads it off the feature.
// `disabled` (measure / AOI-draw modes) stops opening new popovers but keeps an
// already-open one mounted, so an open 3D viewer survives a mode switch.
export const WellClickPopup = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const mapRef = useAtomValue(mapSearchRefAtom);
  const [clicked, setClicked] = useState<ClickedWell | null>(null);

  useEffect(() => {
    if (!mapRef || disabled) return;
    const map = mapRef.getMap();
    const clear = () => setClicked(null);

    const onClick = (e: MapMouseEvent) => {
      const features = queryFeaturesNear(map, e.point, WELL_DOT_LAYER_IDS);
      // Sort nearest-first so the kept set and the anchor are stable across
      // re-clicks instead of following mapbox render order.
      const sorted = features
        .map((f) => {
          const coords = pointCoords(f);
          let dist = Infinity;
          if (coords) {
            const p = map.project(coords);
            dist = (p.x - e.point.x) ** 2 + (p.y - e.point.y) ** 2;
          }
          return { coords, dist, uwi: f.properties?.uwi };
        })
        .sort((a, b) => a.dist - b.dist);

      const uwis: string[] = [];
      let anchor: [number, number] | null = null;
      for (const s of sorted) {
        if (!anchor && s.coords) anchor = s.coords;
        const uwi = s.uwi ? String(s.uwi) : '';
        if (uwi && !uwis.includes(uwi)) uwis.push(uwi);
        if (uwis.length >= MAX_WELLS_PER_CLICK) break;
      }
      if (!uwis.length) {
        // Missed every dot — close any open popover, matching a fresh map click.
        clear();
        return;
      }
      const wells = uwis.reduce(
        (acc, uwi) => {
          acc[uwi] = undefined;
          return acc;
        },
        {} as Record<string, undefined>
      );
      const [lng, lat] = anchor ?? [e.lngLat.lng, e.lngLat.lat];
      const key = [...uwis].sort().join('|');
      setClicked((prev) => {
        // Same well set: keep the wells object so WellPopup doesn't refetch, but
        // re-anchor so clicking another dot of the same well moves the popover.
        if (prev && Object.keys(prev.wells).sort().join('|') === key) {
          return { ...prev, longitude: lng, latitude: lat };
        }
        return { wells, longitude: lng, latitude: lat };
      });
    };

    // Pointer cursor on a well-dot hit; never cleared here. DocumentHoverPopup
    // (mounted just before this) owns clearing on its miss — keep that order or
    // the pointer sticks. Throttled: mousemove fires 60+/sec, each pass hit-tests.
    const onMove = throttle((e: MapMouseEvent) => {
      if (queryFeaturesNear(map, e.point, WELL_DOT_LAYER_IDS).length > 0) {
        map.getCanvas().style.cursor = 'pointer';
      }
    }, 50);

    map.on('click', onClick);
    map.on('mousemove', onMove);
    // A pan clears the popover so it never floats at a stale location. A double-
    // click is left alone: it zooms natively and the geo-anchored popover stays
    // on the dot instead of flashing closed.
    map.on('dragstart', clear);
    return () => {
      onMove.cancel();
      map.off('click', onClick);
      map.off('mousemove', onMove);
      map.off('dragstart', clear);
    };
  }, [mapRef, disabled]);

  if (!clicked) return null;

  return (
    <WellPopup
      wells={clicked.wells}
      latitude={clicked.latitude}
      longitude={clicked.longitude}
      closeButton
      onClose={() => setClicked(null)}
    />
  );
};
